import { useEffect, useRef, useState } from 'react';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';

type CaptureMode = 'mount' | 'visible';

interface LiveTimestampProps {
  /**
   * `mount` — capture the time on first render (used for sections already
   * visible at page load, e.g. the hero).
   * `visible` — wait for the section to scroll into the viewport, then
   * capture the time on the first intersection.
   */
  captureOn: CaptureMode;
}

const FIVE_MINUTES_MS = 5 * 60 * 1000;
const TICK_INTERVAL_MS = 60_000;

function formatHHmm(date: Date): string {
  return date.toLocaleTimeString(undefined, {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Decorative wall-clock timestamp used in the landing-page section gutters.
 * The page itself reads as a notetime entry: each section captures the
 * moment it first scrolled into view, and ticks gently while you linger.
 *
 * Always renders inside an aria-hidden gutter — purely decorative.
 */
export default function LiveTimestamp({ captureOn }: LiveTimestampProps) {
  const reducedMotion = usePrefersReducedMotion();
  const wrapperRef = useRef<HTMLSpanElement | null>(null);

  // For `mount` mode the time is known immediately; for `visible` it stays
  // null until the section first intersects the viewport.
  const [firstVisibleAt, setFirstVisibleAt] = useState<Date | null>(() =>
    captureOn === 'mount' && typeof window !== 'undefined' ? new Date() : null,
  );

  // Whether the section is currently in the viewport. Hero (mount mode)
  // starts visible; visible-mode sections start hidden.
  const [isVisible, setIsVisible] = useState<boolean>(captureOn === 'mount');

  // Wall-clock value used to drive the per-minute tick. We only update this
  // while the section is in view AND within the 5-minute fresh window.
  const [now, setNow] = useState<Date | null>(null);

  // IntersectionObserver: only used for `visible` mode. Captures the first
  // intersection time, then keeps tracking visibility so we know when to tick.
  useEffect(() => {
    if (captureOn !== 'visible') return;
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') return;

    const node = wrapperRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setIsVisible(true);
            setFirstVisibleAt((prev) => prev ?? new Date());
          } else {
            setIsVisible(false);
          }
        }
      },
      { threshold: 0, rootMargin: '-10% 0px' },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [captureOn]);

  // Per-minute tick: only schedule while the section is in view, the first
  // visible time was within the last 5 minutes, and reduced motion is off.
  useEffect(() => {
    if (reducedMotion) return;
    if (!isVisible) return;
    if (!firstVisibleAt) return;
    if (Date.now() - firstVisibleAt.getTime() >= FIVE_MINUTES_MS) return;

    const id = setInterval(() => {
      const next = new Date();
      // Stop ticking once we've crossed the 5-minute freeze threshold.
      if (next.getTime() - firstVisibleAt.getTime() >= FIVE_MINUTES_MS) {
        setNow(null);
        clearInterval(id);
        return;
      }
      setNow(next);
    }, TICK_INTERVAL_MS);

    return () => clearInterval(id);
  }, [reducedMotion, isVisible, firstVisibleAt]);

  // Reserve `00:00` worth of space so the gutter doesn't reflow when the
  // real time pops in on first intersection.
  if (!firstVisibleAt) {
    return (
      <span ref={wrapperRef} aria-hidden>
        <span className="invisible">00:00</span>
      </span>
    );
  }

  // Prefer the live tick (when present) over the captured first-visible time.
  // `now` is only ever set inside the 5-minute fresh window while visible.
  const display = now ?? firstVisibleAt;

  return (
    <span ref={wrapperRef} aria-hidden>
      {formatHHmm(display)}
    </span>
  );
}
